// Imports
import { DateTime } from 'luxon';


// Interfaces
interface FormatDateRangeOptions {
	start: Date;
	end?: Date;
	allDay?: boolean;
	shortenMonth?: boolean;
	dateFormat: 'MDY' | 'DMY';
	timeFormat: '12HR' | '24HR' | '24HR_NO_COLON';
}


/** Intelligently formats a range of date times to a string to match given style parameters */
export default function formatDateRange(options: FormatDateRangeOptions) {
	// Initialize
	const dateTimeComponents: string[] = [];
	const start = DateTime.fromJSDate(options.start);
	const end = options.end ? DateTime.fromJSDate(options.end) : null;
	const sameDate = end === null || start.startOf('day').equals(end.startOf('day'));
	const dateLocale = options.dateFormat === 'MDY' ? 'en-US' : 'en-GB';
	const timeLocale = options.timeFormat === '12HR' ? 'en-US' : 'en-GB';
	const colonReplacement = options.timeFormat === '24HR_NO_COLON' ? '' : ':';
	
	
	// Build formatting options
	const dateFormatOptions = {
		year: 'numeric',
		month: options.shortenMonth ? 'short' : 'long',
		day: 'numeric',
	} as const;
	
	const determineTimeFormatOptions = (minute: number) =>
		({
			hour: 'numeric',
			minute: options.timeFormat !== '12HR' || minute !== 0 ? 'numeric' : undefined,
		}) as const;
	
	
	// Handle all day events
	if (options.allDay) {
		// Initialize
		const dateTimeFormat = new Intl.DateTimeFormat(dateLocale, dateFormatOptions);
		
		
		// Format and return date range
		//  -> Held in a `string`-typed local so the `any` introduced by the `@ts-ignore` below can't leak into the inferred return type
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
		const formattedDateRange: string = dateTimeFormat
			// LATER: Remove end-before-start logic after removing legacy front end
			//  -> https://gitlab.com/sparksuite-family/hoa-express/main-stack/-/issues/6004#note_2189442058
			// LATER: Remove `@ts-ignore` and `eslint-disable-next-line` once our tsconfig lib includes the ES2021 Intl typings that type `Intl.DateTimeFormat.formatRange()`
			// @ts-ignore: `Intl.DateTimeFormat.formatRange()` is absent from our ES2019 lib (typed only in `es2021.intl`)
			.formatRange(start.toJSDate(), end && end < start ? start.toJSDate() : (end?.toJSDate() ?? start.toJSDate()))
			.replace(/–/g, '-')
			.replace(/\u2009/g, ' ');
		
		return formattedDateRange;
	}
	
	
	// Format and add start date and time
	const timeFormatOptions = determineTimeFormatOptions(start.minute);
	const startDate = start.toLocaleString(dateFormatOptions, { locale: dateLocale });
	const startTime = start.toLocaleString(timeFormatOptions, { locale: timeLocale }).replace(':', colonReplacement);
	
	dateTimeComponents.push(`${startDate},`);
	dateTimeComponents.push(startTime);
	
	
	// Handle end time
	if (end && !start.equals(end)) {
		// Add separator
		dateTimeComponents.push('-');
		
		
		// Format and add end date if needed
		if (!sameDate) {
			const endDate = end.toLocaleString(dateFormatOptions, { locale: dateLocale });
			dateTimeComponents.push(`${endDate},`);
		}
		
		
		// Format and add end time
		const timeFormatOptions = determineTimeFormatOptions(end.minute);
		const endTime = end.toLocaleString(timeFormatOptions, { locale: timeLocale }).replace(':', colonReplacement);
		
		dateTimeComponents.push(endTime);
	}
	
	
	// Join and return
	return dateTimeComponents.join(' ');
}
